<template>
  <div class="container">
    <!-- 面包屑导航 -->
    <el-breadcrumb separator="/">
      <el-breadcrumb-item :to="{ path: '/welcome' }">首页</el-breadcrumb-item>
      <el-breadcrumb-item>人事管理</el-breadcrumb-item>
      <el-breadcrumb-item>消息通知</el-breadcrumb-item>
    </el-breadcrumb>

    <!-- 发送区 -->
    <el-card class="main-card">
      <div slot="header" class="toolbar">
        <span class="card-header">发送通知</span>
        <span class="toolbar-tip">消息以"飞鸟中台"应用消息送达成员企微「工作通知」，发出后不可撤回</span>
      </div>

      <el-form label-width="90px" size="medium">
        <el-form-item label="收件范围">
          <el-radio-group v-model="form.scope">
            <el-radio label="all">全员通知（全体在职成员）</el-radio>
            <el-radio label="custom">指定人员</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item v-if="form.scope === 'custom'" label="接收成员">
          <el-select
            v-model="selectedUserids"
            multiple
            filterable
            collapse-tags
            placeholder="按部门分组选择，可输入姓名/userid 过滤"
            style="width: 100%;"
            :loading="loadingContacts"
          >
            <el-option-group v-for="group in groupedContacts" :key="group.departsName" :label="group.departsName">
              <el-option v-for="m in group.members" :key="m.userid" :label="`${m.name}（${m.userid}）`" :value="m.userid"></el-option>
            </el-option-group>
          </el-select>
          <div class="form-tip">通讯录随同步任务每日 07:10 更新，最近同步：{{ fmtTime(syncedAt) }}；新入职当天未同步前不在名单内</div>
        </el-form-item>

        <el-form-item label="标题">
          <el-input v-model="form.title" maxlength="30" show-word-limit placeholder="通知标题（30 字以内）" style="width: 480px;"></el-input>
        </el-form-item>

        <el-form-item label="正文">
          <el-input
            v-model="form.content"
            type="textarea"
            :rows="6"
            maxlength="600"
            show-word-limit
            placeholder="通知正文（600 字以内，支持换行与加粗等 markdown 语法，见下方说明）"
            style="width: 720px;"
          ></el-input>
          <div class="form-tip">{{ syntaxTip }}</div>
        </el-form-item>

        <el-form-item>
          <el-button type="primary" :loading="sending" @click="handleSend">发送</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 发送记录区 -->
    <el-card class="main-card">
      <div slot="header" class="toolbar">
        <span class="card-header">发送记录</span>
        <span class="toolbar-tip">共 {{ total }} 条</span>
      </div>

      <el-table v-loading="loading" :data="records" border stripe>
        <el-table-column label="时间" width="150">
          <template slot-scope="scope">{{ fmtTime(scope.row.createDate) }}</template>
        </el-table-column>
        <el-table-column label="范围" width="120">
          <template slot-scope="scope">
            {{ scope.row.eventType === 'announcement.broadcast' ? '全员' : '指定' }}（{{ scope.row.totalCount }} 人）
          </template>
        </el-table-column>
        <el-table-column label="标题" width="200" show-overflow-tooltip>
          <template slot-scope="scope">{{ scope.row.content.title }}</template>
        </el-table-column>
        <el-table-column label="正文摘要" min-width="240" show-overflow-tooltip>
          <template slot-scope="scope">{{ scope.row.content.description }}</template>
        </el-table-column>
        <el-table-column label="送达统计" width="180">
          <template slot-scope="scope">
            <span>送达 {{ scope.row.sentCount }}</span>
            <span v-if="scope.row.failCount > 0" class="stat-bad"> · 失败 {{ scope.row.failCount }}</span>
            <span v-if="scope.row.invalidCount > 0" class="stat-bad"> · 无效 {{ scope.row.invalidCount }}</span>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="100">
          <template slot-scope="scope">
            <el-tag :type="statusTag[scope.row.status] || 'info'" size="small">{{ statusText[scope.row.status] || scope.row.status }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="90" fixed="right">
          <template slot-scope="scope">
            <el-button type="text" size="small" @click="openDetail(scope.row)">明细</el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="pager">
        <el-pagination
          background
          layout="total, prev, pager, next, jumper"
          :total="total"
          :page-size="pagesize"
          :current-page="pagenum"
          @current-change="handlePageChange"
        ></el-pagination>
      </div>
    </el-card>

    <!-- 明细抽屉 -->
    <el-drawer :visible.sync="detailVisible" size="55%" :title="current ? `投递明细：${current.content.title}` : '投递明细'">
      <div v-loading="detailLoading" class="drawer-body">
        <template v-if="current">
          <div class="detail-content">
            <div class="detail-title">{{ current.content.title }}</div>
            <pre class="detail-desc">{{ current.content.description }}</pre>
          </div>
          <el-table :data="current.recipients" border stripe max-height="520" size="small">
            <el-table-column type="index" label="#" width="46"></el-table-column>
            <el-table-column prop="employeeName" label="姓名" width="110"></el-table-column>
            <el-table-column prop="wecomUserId" label="userid" width="160" show-overflow-tooltip></el-table-column>
            <el-table-column label="状态" width="110">
              <template slot-scope="scope">
                <el-tag :type="recipTag[scope.row.status] || 'info'" size="mini">{{ recipText[scope.row.status] || scope.row.status }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="error" label="失败原因" show-overflow-tooltip>
              <template slot-scope="scope">{{ scope.row.error || '—' }}</template>
            </el-table-column>
          </el-table>
        </template>
      </div>
    </el-drawer>
  </div>
</template>

<script>
export default {
  name: 'RenshiNotify',
  data () {
    return {
      // 发送表单
      form: { scope: 'all', title: '', content: '' },
      selectedUserids: [],
      sending: false,

      // 选人数据源
      contactList: [],
      contactCount: 0,
      syncedAt: null,
      loadingContacts: false,

      // 记录列表
      records: [],
      total: 0,
      pagenum: 1,
      pagesize: 20,
      loading: false,

      // 明细抽屉
      detailVisible: false,
      detailLoading: false,
      current: null,

      statusTag: { sent: 'success', partial: 'warning', failed: 'danger', skipped: 'info' },
      statusText: { sent: '成功', partial: '部分送达', failed: '失败', skipped: '未发送' },
      recipTag: { sent: 'success', failed: 'danger', skipped_unbound: 'info', skipped_invalid: 'warning', pending: 'info' },
      recipText: { sent: '送达', failed: '失败', skipped_unbound: '未绑定企微', skipped_invalid: '不可送达', pending: '待发送' },
      // 企微 markdown 有限语法说明（插值渲染，避免 <font> 被模板解析为真实标签）
      syntaxTip: '支持企微 markdown 有限语法：**加粗**、## 小标题、> 引用、换行；彩色字 <font color="info">绿色</font> / <font color="comment">灰色</font> / <font color="warning">橙红</font>；不支持表格与图片'
    }
  },
  computed: {
    contactMap () {
      const map = {}
      this.contactList.forEach(m => { map[m.userid] = m })
      return map
    },
    groupedContacts () {
      const groups = {}
      this.contactList.forEach(m => {
        const key = m.departsName || '未分配部门'
        ;(groups[key] = groups[key] || []).push(m)
      })
      return Object.keys(groups).sort().map(departsName => ({ departsName, members: groups[departsName] }))
    }
  },
  created () {
    this.loadContacts()
    this.getList()
  },
  methods: {
    async loadContacts () {
      this.loadingContacts = true
      try {
        const { data: res } = await this.$http.get('renshi/notify/contacts')
        if (res.meta.status !== 200) return this.$message.error(res.meta.msg)
        this.contactList = res.data.list
        this.contactCount = res.data.count
        this.syncedAt = res.data.syncedAt
      } finally {
        this.loadingContacts = false
      }
    },
    async getList () {
      this.loading = true
      try {
        const { data: res } = await this.$http.get('renshi/notify/list', { params: { pagenum: this.pagenum, pagesize: this.pagesize } })
        if (res.meta.status !== 200) return this.$message.error(res.meta.msg)
        this.records = res.data.list
        this.total = res.data.total
      } finally {
        this.loading = false
      }
    },
    handlePageChange (page) {
      this.pagenum = page
      this.getList()
    },
    async handleSend () {
      const title = this.form.title.trim()
      const content = this.form.content.trim()
      if (!title) return this.$message.error('请输入标题')
      if (!content) return this.$message.error('请输入正文')
      if (this.form.scope === 'custom' && this.selectedUserids.length === 0) return this.$message.error('请选择接收成员')

      // 二次确认（发出后不可撤回，唯一拦截点）
      const targetText = this.form.scope === 'all'
        ? `全体在职成员（通讯录 ${this.contactCount} 人）`
        : `选中的 ${this.selectedUserids.length} 名成员`
      const confirmed = await this.$confirm(`将向${targetText}发送应用消息，发出后不可撤回，是否确认？`, '发送确认', {
        confirmButtonText: '确认发送',
        cancelButtonText: '再想想',
        type: 'warning'
      }).catch(() => false)
      if (!confirmed) return

      this.sending = true
      try {
        const payload = { scope: this.form.scope, title, content }
        if (this.form.scope === 'custom') {
          payload.recipients = this.selectedUserids.map(uid => ({ userid: uid, name: this.contactMap[uid] ? this.contactMap[uid].name : '' }))
        }
        const { data: res } = await this.$http.post('renshi/notify/send', payload)
        if (res.meta.status !== 200) return this.$message.error(res.meta.msg)
        this.$message.success(res.meta.msg || '发送完成')
        if (res.data && res.data.invalidCount > 0) {
          this.$message.warning(`有 ${res.data.invalidCount} 人未能送达（不在应用可见范围或未同步），详情见发送记录`)
        }
        // 重置正文，保留范围选择
        this.form.title = ''
        this.form.content = ''
        this.selectedUserids = []
        this.pagenum = 1
        this.getList()
      } finally {
        this.sending = false
      }
    },
    async openDetail (row) {
      this.detailVisible = true
      this.detailLoading = true
      this.current = null
      try {
        const { data: res } = await this.$http.get(`renshi/notify/detail/${row._id}`)
        if (res.meta.status !== 200) return this.$message.error(res.meta.msg)
        this.current = res.data.notification
      } finally {
        this.detailLoading = false
      }
    },
    fmtTime (t) {
      if (!t) return '—'
      const d = new Date(t)
      const p = n => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`
    }
  }
}
</script>

<style scoped>
.form-tip {
  font-size: 12px;
  color: #909399;
  line-height: 1.6;
}
.stat-bad {
  color: #f56c6c;
}
.pager {
  margin-top: 14px;
  text-align: right;
}
.drawer-body {
  padding: 0 20px 20px;
}
.detail-content {
  margin-bottom: 14px;
}
.detail-title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}
.detail-desc {
  margin: 8px 0 0;
  font-family: inherit;
  font-size: 13px;
  color: #606266;
  white-space: pre-wrap;
  word-break: break-all;
  line-height: 1.7;
  background: #f5f7fa;
  border-radius: 4px;
  padding: 10px 12px;
}
</style>
